import { thetaApi, dataOf, pageOf, MAX_PAGE_LIMIT, pageLimit } from "./theta";
import type { CustomEmoji, GroupRef, Page, ShortcodeAvailability } from "@theta/api-client";

/** A group id or slug, in one path segment (D8). Plain numbers accepted. */
type Ref = GroupRef | number;

/**
 * A group's custom emoji.
 *
 * Every call takes a group REF — an id or a slug in the same path segment
 * (D8) — so a caller that has one never resolves it to the other.
 */
/**
 * ONE PAGE, AT THE CONTRACT'S OWN MAXIMUM.
 *
 * This defaulted to `limit = 200` and `GET /groups/{ref}/emojis` caps `limit`
 * at 100 like every other paged read, so all five call sites 400ed on every
 * request — an empty custom tab in the reaction picker, no `:shortcode:`
 * autocomplete, and an admin told a group with 30 emoji had none. The default
 * is the ceiling now, and anything a caller passes is clamped to it rather
 * than sent and refused; a group with more emoji than one page is paged
 * through `nextCursor` like every other collection.
 */
export function getCustomEmojis(
  ref: Ref,
  limit: number = MAX_PAGE_LIMIT
): Promise<Page<CustomEmoji>> {
  return thetaApi.http
    .GET("/groups/{ref}/emojis", {
      params: { path: { ref: String(ref) }, query: { limit: pageLimit(limit) } },
    })
    .then(pageOf<CustomEmoji>);
}

export function createCustomEmoji(
  ref: Ref,
  data: { shortcode: string; uploadId: number }
): Promise<CustomEmoji> {
  return thetaApi.http
    .POST("/groups/{ref}/emojis", {
      params: { path: { ref: String(ref) } },
      body: data as never,
    })
    .then(dataOf<CustomEmoji>);
}

/** Addressed by SHORTCODE, which is what makes it unique within the group. */
export async function deleteCustomEmoji(ref: Ref, shortcode: string): Promise<void> {
  await thetaApi.http
    .DELETE("/groups/{ref}/emojis/{shortcode}", {
      params: { path: { ref: String(ref), shortcode } },
    })
    .then(dataOf<null>);
}

/**
 * Is this shortcode free? `conflict` is a closed vocabulary — `standard`,
 * `group` or `invalid` — rather than a message to match on.
 */
export function checkShortcode(ref: Ref, shortcode: string): Promise<ShortcodeAvailability> {
  return thetaApi.http
    .GET("/groups/{ref}/emojis/{shortcode}", {
      params: { path: { ref: String(ref), shortcode } },
    })
    .then(dataOf<ShortcodeAvailability>);
}

export type { CustomEmoji };
